import { Component, inject, Input, OnChanges, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { Dialog } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { DacGeneralParamsService } from '../../../dac-general-params/dac-general-params.service';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import {
  DynaFields,
  DynaFieldType,
  ParametrageGeneralDac,
  ParamGeneralDacMixedItemType,
  ParamGeneralDacMixedReorderItem,
  ParamGeneralDacPlacement,
  ParamGeneralStatus,
} from '@/types';

interface MergedRow {
  kind: 'FIELD' | 'PLACEMENT';
  id: number;
  order: number;
  label: string;
  typeLabel: string;
  raw: DynaFields | ParamGeneralDacPlacement;
}

/**
 * A section's (document dynamique's) whole field list, local fields and shared/global
 * variables shown and reordered together as ONE sequence - the two used to keep independent
 * order counters (each restarting at 1, 2, 3...), which made it impossible to place a shared
 * variable between two local fields (or vice versa). Replaces the old pairing of a plain
 * fields datatable + a separate global-variables-panel.
 *
 * Local fields are owned by DacRepository (the parent screen still handles create-via-drawer/
 * edit/delete); this component only reorders them alongside placements and offers the
 * search-or-create "add" control. Placements (attach/remove/their own order) are fully
 * self-contained here, same as the panel this replaces.
 */
@Component({
  selector: 'sm-section-variables-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, AutoCompleteModule, Dialog, TextareaModule, TooltipModule, TranslateModule],
  templateUrl: './section-variables-list.component.html',
  styleUrls: ['./section-variables-list.component.scss'],
})
export class SectionVariablesListComponent implements OnChanges {
  // Bound from async-piped observables in the parent template, which emit `null` before the
  // store's first value.
  @Input() dynaDocsId?: number | null;
  @Input() dynaDocsVer?: number | null;

  // Local field lifecycle (create-via-drawer / edit / delete) stays owned by the parent, which
  // already has the field-editor drawer wired up.
  @Output() editLocalField = new EventEmitter<DynaFields>();
  @Output() deleteLocalField = new EventEmitter<number>();
  // Typed name matched no existing global variable - parent creates it as a new local field.
  // order is the position it should land in among the merged list, computed here so it never
  // collides with an existing local field's or placement's order.
  @Output() createLocalField = new EventEmitter<{ name: string; order: number }>();

  private readonly service = inject(DacGeneralParamsService);
  protected readonly dacRepository = inject(DacRepository);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  protected loading = false;
  protected placements: ParamGeneralDacPlacement[] = [];
  protected availableVariables: ParametrageGeneralDac[] = [];

  protected searchQuery = '';
  protected suggestions: ParametrageGeneralDac[] = [];

  // Description-override dialog, for a shared variable's placement in this section only.
  protected descriptionDialogVisible = false;
  protected descriptionDialogPlacement: ParamGeneralDacPlacement | null = null;
  protected descriptionDialogValue = '';
  protected descriptionDialogBasePlaceholder = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dynaDocsId'] || changes['dynaDocsVer']) {
      this.refresh();
    }
  }

  protected get canManage(): boolean {
    return !!this.dynaDocsId && !!this.dynaDocsVer;
  }

  // Local fields come straight from the store (read live, same non-async pattern already used
  // by the fields datatable this replaces) so edits/creates/deletes made elsewhere show up
  // immediately; placements are this component's own state.
  protected get rows(): MergedRow[] {
    const fieldRows: MergedRow[] = this.dacRepository.getFields().map(field => ({
      kind: 'FIELD' as const,
      id: field.dynaFieldsId,
      order: field.dynaFieldsOrder ?? 0,
      label: field.label || field.name,
      typeLabel: this.fieldTypeLabel(field.type),
      raw: field,
    }));

    const placementRows: MergedRow[] = this.placements.map(placement => ({
      kind: 'PLACEMENT' as const,
      id: placement.placementId,
      order: placement.ordre ?? 0,
      label: placement.paramGeneralLabel || placement.paramGeneralName || '',
      typeLabel: 'dynamicDocument.globalVariables.badge',
      raw: placement,
    }));

    return [...fieldRows, ...placementRows].sort((a, b) => a.order - b.order);
  }

  private fieldTypeLabel(type: DynaFieldType): string {
    switch (type) {
      case DynaFieldType.TABLE:
        return 'dynamicDocument.form.table';
      case DynaFieldType.LIST:
        return 'dynamicDocument.form.list';
      case DynaFieldType.INTEGER:
        return 'dynamicDocument.form.number';
      case DynaFieldType.DECIMAL:
        return 'dynamicDocument.form.decimal';
      case DynaFieldType.DATE:
        return 'dynamicDocument.form.date';
      case DynaFieldType.DATETIME:
        return 'dynamicDocument.form.dateTime';
      case DynaFieldType.SYSTEM:
        return 'dynamicDocument.form.system';
      default:
        return 'dynamicDocument.form.text';
    }
  }

  // Refreshes placements + the pickable global-variable library. Local fields don't need a
  // fetch here - they're read live from the store.
  refresh(): void {
    if (!this.dynaDocsId || !this.dynaDocsVer) {
      this.placements = [];
      this.availableVariables = [];
      return;
    }

    this.loading = true;
    const placements$: Observable<ParamGeneralDacPlacement[]> = this.service
      .getPlacementsByDynaDocs(this.dynaDocsId, this.dynaDocsVer)
      .pipe(catchError((): Observable<ParamGeneralDacPlacement[]> => of([])));

    // Full library, not just variables already linked to this document's model - a variable
    // created for one DAC template must stay pickable when configuring any other section.
    const library$: Observable<ParametrageGeneralDac[]> = this.service
      .getAllList()
      .pipe(catchError((): Observable<ParametrageGeneralDac[]> => of([])));

    forkJoin([placements$, library$])
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((result: [ParamGeneralDacPlacement[], ParametrageGeneralDac[]]) => {
        const [placements, library] = result;
        this.placements = [...placements].sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
        const placedIds = new Set(this.placements.map(p => p.paramGeneralDacId));
        this.availableVariables = library.filter(v => v.paramGeneralStatus !== ParamGeneralStatus.NON_ACTIF && !placedIds.has(v.id));
      });
  }

  protected search(event: any): void {
    const query = (event.query ?? '').trim().toLowerCase();
    this.suggestions = !query
      ? this.availableVariables
      : this.availableVariables.filter(
          v => (v.paramGeneralLabel ?? '').toLowerCase().includes(query) || (v.paramGeneralName ?? '').toLowerCase().includes(query)
        );
  }

  // Picking a suggestion attaches it right away - PrimeNG sets the bound ngModel to the
  // selected object on select, so we don't read searchQuery back afterward, just reset it.
  protected onSuggestionSelect(event: any): void {
    const variable = event.value as ParametrageGeneralDac;
    this.searchQuery = '';
    this.suggestions = this.availableVariables;
    this.attachVariable(variable.id);
  }

  // The "+" button: free text that didn't come from picking a suggestion - either it exactly
  // matches an existing variable's label (attach it), or it doesn't (hand it up to the parent
  // to create as a new local field, appended after everything currently in the section).
  protected confirmAdd(): void {
    const query = this.searchQuery.trim();
    if (!query) return;

    const exactMatch = this.availableVariables.find(v => (v.paramGeneralLabel ?? '').toLowerCase() === query.toLowerCase());

    if (exactMatch) {
      this.attachVariable(exactMatch.id);
    } else {
      const nextOrder = this.rows.reduce((max, row) => Math.max(max, row.order), 0) + 1;
      this.createLocalField.emit({ name: query, order: nextOrder });
    }

    this.searchQuery = '';
    this.suggestions = [];
  }

  private attachVariable(paramGeneralDacId: number): void {
    if (!this.dynaDocsId || !this.dynaDocsVer) return;
    // ordre intentionally omitted - the backend appends after the highest order currently
    // used by either local fields or placements in this section.
    this.runMutation(
      this.service.addPlacement({
        paramGeneralDacId,
        dynaDocsId: this.dynaDocsId,
        dynaDocsVer: this.dynaDocsVer,
      })
    );
  }

  protected removePlacement(row: MergedRow): void {
    const placement = row.raw as ParamGeneralDacPlacement;
    this.runMutation(this.service.removePlacement(placement.placementId));
  }

  protected deleteField(row: MergedRow): void {
    this.deleteLocalField.emit(row.id);
  }

  protected editField(row: MergedRow): void {
    this.editLocalField.emit(row.raw as DynaFields);
  }

  // Opens the description-override dialog for a shared variable's placement in this section.
  // Starts empty (not overridden) unless this placement already has its own override - the
  // effective (fallback-applied) description is shown as a placeholder/hint either way, so
  // it's clear what leaving the box empty means.
  protected editPlacementDescription(row: MergedRow): void {
    const placement = row.raw as ParamGeneralDacPlacement;
    this.descriptionDialogPlacement = placement;
    this.descriptionDialogValue = placement.placementDescription ?? '';
    this.descriptionDialogBasePlaceholder = placement.paramGeneralDescription ?? '';
    this.descriptionDialogVisible = true;
  }

  protected saveDescriptionOverride(): void {
    if (!this.descriptionDialogPlacement) return;
    this.runDescriptionMutation(this.descriptionDialogValue.trim());
  }

  // Blank clears the override on the backend (reverts to the base variable's description) -
  // same effect as saving an empty box, offered as its own button so it's explicit.
  protected clearDescriptionOverride(): void {
    if (!this.descriptionDialogPlacement) return;
    this.runDescriptionMutation('');
  }

  private runDescriptionMutation(description: string): void {
    const placementId = this.descriptionDialogPlacement?.placementId;
    if (!placementId) return;

    this.loading = true;
    this.service
      .updatePlacement(placementId, { description })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.descriptionDialogVisible = false;
          this.descriptionDialogPlacement = null;
          this.refresh();
        },
        error: () => this.showError(this.translate.instant('dynamicDocument.errors.saved')),
      });
  }

  protected moveUp(index: number): void {
    if (index <= 0) return;
    this.swapAndSave(index, index - 1);
  }

  protected moveDown(index: number): void {
    if (index >= this.rows.length - 1) return;
    this.swapAndSave(index, index + 1);
  }

  private swapAndSave(indexA: number, indexB: number): void {
    const reordered = [...this.rows];
    [reordered[indexA], reordered[indexB]] = [reordered[indexB], reordered[indexA]];
    this.persistOrder(reordered);
  }

  // Renumbers the WHOLE merged list 1..n and persists it in one call - this is also what
  // self-heals any earlier order collision between a local field and a placement.
  private persistOrder(reordered: MergedRow[]): void {
    if (!this.dynaDocsId || !this.dynaDocsVer) return;

    const items: ParamGeneralDacMixedReorderItem[] = reordered.map((row, i) => ({
      itemType: row.kind === 'FIELD' ? ParamGeneralDacMixedItemType.FIELD : ParamGeneralDacMixedItemType.PLACEMENT,
      itemId: row.id,
      order: i + 1,
    }));

    this.loading = true;
    this.service
      .reorderMixed(this.dynaDocsId, this.dynaDocsVer, items)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          reordered.forEach((row, i) => {
            const newOrder = i + 1;
            if (row.kind === 'FIELD') {
              const field = row.raw as DynaFields;
              this.dacRepository.updateField({ ...field, dynaFieldsOrder: newOrder });
            } else {
              (row.raw as ParamGeneralDacPlacement).ordre = newOrder;
            }
          });
        },
        error: () => this.showError(this.translate.instant('dynamicDocument.errors.saved')),
      });
  }

  // Shared "run a write, show an error on failure, refresh on success" pattern. Uses
  // subscribe's next/error handlers rather than a catchError-to-null fallback: the
  // add/remove endpoints can return 204 No Content, which Angular parses as `null` - a
  // null-as-failure sentinel would then misreport a successful call as failed.
  private runMutation<T>(request$: Observable<T>): void {
    this.loading = true;
    request$.pipe(finalize(() => (this.loading = false))).subscribe({
      next: () => this.refresh(),
      error: () => this.showError(this.translate.instant('dynamicDocument.errors.saved')),
    });
  }

  private showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Erreur', detail });
  }
}
